/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import {
  Button,
  Divider,
  Modal,
  CardContent,
  Stack,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';

import MainCard from "components/MainCard";
import useModals from 'components/modals/useModal';
import { modals } from 'components/modals/Modals'; 
import { useEnumApi } from "contexts/EnumApiContext";
import CustomFormEnumSelect from "components/select/CustomFormEnumSelect";
import { boxStyle, Grid2, inputStyle, SectionTitle } from "./CommonModule";
import { CustomMuiDateTimePicker } from "components/date_picker/CustomMuiDateTimePicker";
import EquipmentAutocomplete from "components/EquipmentAutocomplete";
import dayjs from "utils/dayjs";
import { customFileAxios } from "components/axios/customAxios";
import { getEquipmentCategoriesByQuery, getEquipmentComponentLocationByCategory } from "components/axios/equipmentCategoryAxios";
import { useQuery } from "@tanstack/react-query";
import MaintenanceInputPartsRow from "./MaintenanceInputPartsRow";
import { useTranslation } from "react-i18next";
import { getEquipmentsById } from "components/axios/equipmentAxios";
import { isValidDateTimeFormat } from "components/date_picker/dateUtils";
import { PERMISSIONS } from "config/permissions";
import PermissionButton from "components/PermissionButton";

const MaintenanceTaskAndAlertModal = ({onRefresh, equipmentId, type}) => {
  const { t } = useTranslation();
  const { getEnumValueByEnumName } = useEnumApi();
  const { name } = useSelector(state => state.user);
  
  // modal 관련
  const { openModal } = useModals();
  const handleAlertClick = (alertMessage) => {
      openModal(modals.alertModal, {
          onClose : () => {
          },
          message: alertMessage,
      });
  };

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [maintenanceTask, setMaintenanceTask] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);

  const { control, register, handleSubmit, reset, setValue, formState : {isDirty, errors } } = useForm({});
  const regexDateTime = RegExp(/^\d{4}.(0[1-9]|1[012]).(0[1-9]|[12][0-9]|3[01])T(0[0-9]|1[0-9]|2[0-3]):(0[1-9]|[0-5][0-9])$/);
  const today = dayjs().tz(process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ');

  useEffect(() => {
    if (open && equipmentId) {
      const fetchDetail = async () => {
        try {
          const res = await getEquipmentsById(equipmentId);
          console.log(res)
          const data = {
            equipmentId : res.id
            , equipmentName: res.name
            , equipmentNo: res.equipmentNo
            , stationName : res.station?.name
            , subwayLine:res.station?.workingPart?.subwayLine
            , categoryId:res.equipmentCtgr?.id
          };
          setValue('equipment', data);
          // setInputValue(`${res.station?.name} - ${res.name}`);
          // setOptions([data]);
        } catch (e) {
          console.error(e);
        }
      };
      fetchDetail();
    }
  }, [open, equipmentId]);

  useEffect(() => {
    if (open) {
      setValue("alertedAt", today);
      setValue("workingBgnAt", today);
    }
  }, [open]);
    
  const selectedEquipment = useWatch({
    control,
    name: "equipment", // EquipmentAutocomplete에 넘겨준 name과 일치해야 합니다.
  });
  // console.log(selectedEquipment)
  const category3Id = selectedEquipment?.categoryId;

  // 🚀 [공통 API] 모든 행이 공유하는 설비구성부, 구성부위치 데이터
  const { data: componentData } = useQuery({
    queryKey: ['componentDatas', category3Id],
    queryFn: async () => {
        if (!category3Id) return null;
        const param = { "parent.id": category3Id, depth: 4, size : 100, sort : "name,asc" };
        const [
            componentsResult,
            componentLocationsResult,
        ] = await Promise.all([
            getEquipmentCategoriesByQuery(param),
            getEquipmentComponentLocationByCategory(category3Id)
        ]);

        return {
            components : (componentsResult),
            locations: (componentLocationsResult)
        };
    },
    enabled: !!category3Id,
  });

  // 공통 목록 가공 (useMemo 활용 가능)
  const componentOptions = componentData?.components?._embedded?.equipmentCategories || []; // 예: [{ value: '1', label: '옵션 A' }]
  const locationOptions = componentData?.locations || [];

  // console.log(componentOptions)
  // console.log(locationOptions)

  const nextID = useRef(1);
  const [inputFiles, setInputFiles] = useState([]);

  function addInputFile() {
    const input = { id: nextID.current, file: {}, rFile: null };
    setInputFiles([...inputFiles, input]);
    nextID.current += 1;
  }

  function deleteInputFile(index) {
      setInputFiles(inputFiles.filter(item => item.id !== index));
  }

  function handleInputFileChange(e, index) {
      setInputFiles(prev =>
        prev.map((item, i) =>
            i === index
                ? {
                      ...item,
                      file: e.target.files[0],
                      rFile: null // DB에서 불러온 file 객체
                  }
                : item
        )
      );
  }

  const nextPartId = useRef(1);
  const [inputParts, setInputParts] = useState([]);

  function addInputPart() {
      const input = {
          id: nextPartId.current,
          part : {
            id : 0,
            componentId: "",
            componentLocationId: "",
            partId: "",
            partName: "",
            subPart: "",
            partLifeSpanTime: ""
          }
      };

      setInputParts(prev => [...prev, input]);
      nextPartId.current += 1;
  }

  function deleteInputPart(id) {
      setInputParts(prev => prev.filter(item => item.id !== id));
  }

  function handlePartChange(id, key, value) {
      setInputParts(prev =>
          prev.map(item => {
              if (item.id === id) {
                // 1. 기본적으로 기존 part 객체를 복사하면서 새로운 key/value를 덮어씁니다.
                let updatedPart = { ...item.part, [key]: value };

                // 2. ⭐ 만약 설비구성부(componentId)가 바뀌면 대상자재(partId)를 함께 초기화합니다.
                if (key === "componentId") {
                    updatedPart = { ...updatedPart, partId: "" };
                }

                // 3. 최종적으로 item 객체 안의 part를 교체하여 반환합니다.
                return {
                    ...item,
                    part: updatedPart
                };
              }
              return item;
            }
          )
      );
  }

  const onSubmit = (data) => {
    setFileLoading(true);
    console.log("form data:", data);

    if (data.alertedAt != null && data.alertedAt != '' && !isValidDateTimeFormat(data.alertedAt)) {
      handleAlertClick(t('message.common.alert.msg.enter.inspection.datetime.accurate'));
      return;
    }
  
    if (data.workingBgnAt != null && data.workingBgnAt != '' && !isValidDateTimeFormat(data.workingBgnAt)) {
      handleAlertClick(t('message.common.alert.work.start.datetime.input'));
      setFileLoading(false);
      return;
    }

    if (data.workingEndAt != null && data.workingEndAt != '' && !isValidDateTimeFormat(data.workingEndAt)) {
      handleAlertClick(t('message.common.alert.work.end.datetime.input'));
      setFileLoading(false);
      return;
    }
    
    if (data.alertedAt > today) {
      handleAlertClick(t('message.common.alert.inspection.request.datetime.future.invalid'));
      setFileLoading(false);
      return;
    }

    if (data.alertedAt > data.workingBgnAt) {
      handleAlertClick(t('message.common.alert.work.start.datetime.invalid'));
      setFileLoading(false);
      return;
    }

    if (data.workingEndAt !== null && data.workingEndAt != '') {
      if (data.workingBgnAt > data.workingEndAt) {
        handleAlertClick(t('message.common.alert.work.end.datetime.not.equal.work.begin.datetime'));
        setFileLoading(false);
        return;
      }
    }

    // 🚨 [체크 1] 최소 1개 이상의 행이 존재하는지 검증
    if (!inputParts || inputParts.length === 0) {
      handleAlertClick(t('message.common.alert.at.least.one.input.detail.required'));
      setFileLoading(false);
      return;
    }

    // 🚨 [체크 2] 필수 필드가 모두 채워졌는지 검증 (부품명은 필수에서 제외)
    // .every()는 배열의 모든 요소가 조건을 만족해야 true를 반환합니다.
    const isAllValid = inputParts.every((item, index) => {
      const hasComponent = !!item.part.componentId;
      const hasLocation = !!item.part.componentLocationId;
      const hasPart = !!item.part.partId;

      // 하나라도 비어있으면 false 반환
      return hasComponent && hasLocation && hasPart;
    });

    if (!isAllValid) {
      // .findIndex()를 사용하면 조건에 맞지 않는 첫 번째 행의 인덱스를 찾을 수 있습니다.
      const invalidIndex = inputParts.findIndex(item => 
        !item.part.componentId || !item.part.componentLocationId || !item.part.partId
      );

      if (invalidIndex !== -1) {
        handleAlertClick(t('message.common.alert.missing.required.input.fields',{ num: (invalidIndex + 1) }));
        setFileLoading(false);
        return;
      }

      // handleAlertClick("추가된 내역 중 설비구성부, 구성부위치, 대상자재를 모두 선택하지 않은 행이 있습니다. 확인 후 다시 시도해주세요.");
      // setFileLoading(false);
      // return;
    }

    let dto = {
      equipment : {id: data.equipment?.equipmentId},
      // equipmentCtgr : {id: data.componentId},
      cost : 0,
      // mntSpType : data.mntSpType,
      importantYn : data.importantYn,
      // position : data.position,
      // recordType : data.recordType,
      remark : data.remark,
      // status : data.status,
      trouble : data.trouble,
      // troubledAt : data.troubledAt,
      type : data.type,
      // usedMethod : data.usedMethod,
      worker : data.worker,
      // alertedAt : dayjs(data.alertedAt).format("YYYY-MM-DDTHH:mm:ssZ"),
      // workingBgnAt : dayjs(data.workingBgnAt).format("YYYY-MM-DDTHH:mm:ssZ"),
      // workingEndAt : data.workingEndAt ? dayjs(data.workingEndAt).format("YYYY-MM-DDTHH:mm:ssZ") : null,
      alertedAt : data.alertedAt,
      workingBgnAt : data.workingBgnAt,
      workingEndAt : data.workingEndAt,
    }

    if (!data.workingEndAt) {
      dto.status = 'O2_P';
    } else {
      dto.status = 'O3_C';
    }

    let statusReport = {};
    dto.statusReport = statusReport;
    
// console.log(dto);

    let formData = new FormData();

    let fileCnt = 0;
    if(inputFiles) {
      for (var l=0;l<inputFiles.length;l++) {
          if (inputFiles[l].file && inputFiles[l].file.name) {
              formData.append("statusFiles", inputFiles[l].file);
              fileCnt++;
          }
      }
    }

    let equipmentParts = [];
    let isDuple = false;
    if(inputParts && inputParts.length > 0) {
      for (var m=0;m<inputParts.length;m++) {
        console.log(inputParts[m])
          if (inputParts[m].part.partId) {
            let dupleChk = inputParts.filter(item => item.part.partId === inputParts[m].part.partId);
            //console.log(dupleChk);
            if (dupleChk.length > 1) {
              isDuple = true;
              break;
            } else {
              if (inputParts[m].part.id !== 0) {
                equipmentParts.push({
                  id:inputParts[m].part.id, 
                  // equipmentCtgr : {id: inputParts[m].componentId},
                  componentLocation : {id : inputParts[m].part.componentLocationId},
                  subPart : inputParts[m].part.subPart
                })
              } else {
                equipmentParts.push({
                  lifeSpanTime:inputParts[m].part.partLifeSpanTime, 
                  masterEquipmentPart:{
                    id:inputParts[m].part.partId
                  }, 
                  // equipmentCtgr : {id: inputParts[m].componentId},
                  componentLocation : {id : inputParts[m].part.componentLocationId},
                  subPart : inputParts[m].part.subPart
                })
              }
            }
          }
      }
      dto.equipmentParts = equipmentParts;
    }

    if (isDuple) {
      handleAlertClick(t('message.common.alert.duplication.material.check.again'));
      setFileLoading(false);
      return;
    }

    // if (data.status === 'O3_C' && equipmentParts.length === 0) {
    if (equipmentParts.length === 0) {
      handleAlertClick(t('message.common.alert.material.select.add'));
      setFileLoading(false);
      return;
    }

console.log(dto);

    formData.append("dto", new Blob([JSON.stringify(dto)], {type: "application/json"}) );

    openModal(modals.confirmModal, {
      onSubmit : (value) => {
        try {
            const res = customFileAxios.post(`/api/maintenanceTasks`, formData).then((res)=>{
            console.log(res);
            handleAlertClick(t('message.common.alert.write.done'));
            setFileLoading(false);
            if (onRefresh) onRefresh();
            reset();
            nextID.current = 1;
            setInputFiles([]);
            nextPartId.current = 1;
            setInputParts([]);
            handleClose();
            //navigate('../modify/'+res.data.id+window.location.search);
        }).catch((err)=>{
            // console.log(err);
            setFileLoading(false);
            handleAlertClick(err.response?.data?.message || err.message);
        });
        // return res.data;
    } catch (error) {
        console.error("error:", error);
        throw error;
    }
      },
      onClose : () => {
          console.log('close');
          setFileLoading(false);
      },
      message: t('message.common.alert.write.question'),
    });
  };

  const setEndAtNow = () => {
    setValue("workingEndAt", today, { shouldValidate: true, shouldDirty: true });
  }

  return (
    <>
      {type && type === 'icon' &&
      <PermissionButton
          permission={PERMISSIONS.MAINTENANCE_TASK_WRITE}
          variant="contained" onClick={handleOpen} style={{padding:"3px", minWidth:"unset", margin: "3px"}} color="grey"
      >
          <span className="material-symbols-outlined" style={{color:"#fff"}}>build</span>
      </PermissionButton>
      }
      {(type === null || type === undefined || type === 'button') &&
      <PermissionButton
          permission={PERMISSIONS.MAINTENANCE_TASK_WRITE}
          variant="contained" onClick={handleOpen} style={{minWidth:"unset", margin: "3px", border: "1px solid #2672f7", color: "#fff"}}
      >
          {t('message.common.btn.maintenance.write')}
      </PermissionButton>
      }

      <Modal className="pop_box pop_box1 renew_pop_box" open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <MainCard modal darkTitle content={false} sx={{ width: 800 }}>
          <div className="modal-header">🚨 {t('message.common.btn.maintenance.write')}</div>

          <CardContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
            <form onSubmit={handleSubmit(onSubmit)}>

              {/* ================= 기본 정보 ================= */}
              <SectionTitle title={t('message.common.title.basic.info.and.equipment.selection')} />

              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                  {t('message.common.subtitle.target.equipment')} (Equipment)
                </Typography>
                
                <EquipmentAutocomplete control={control} name="equipment" />
              </Box>

              <Grid2>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                    {t('message.common.subtitle.inspection.request.datetime')}
                  </Typography>

                  <Controller
                    control={control}
                    name="alertedAt"
                    rules={{
                      required: t('message.common.alert.msg.enter.inspection.datetime'),
                      validate: (value) => {
                        if (!value) return true;
                        // 만약 서버 포맷(T 포함)으로 이미 정제된 값이라면 통과
                        if (value.includes("T")) return true; 
                        
                        // 사용자가 입력창에 남겨둔 임시 텍스트 검증
                        const normalized = value.trim().replace(/-/g, ".");
                        const parsed = dayjs(normalized, "YYYY.MM.DD HH:mm", true);
                        
                        return (normalized.length === 16 && parsed.isValid()) || t('message.common.error.text.msg.invalid.date.format');
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <CustomMuiDateTimePicker field={field} fieldState={fieldState} />
                    )}
                  />
                </Box>

                <Box sx={{ flex: 1 , marginTop: "5px"}}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                    {t('message.common.subtitle.actionType')}
                  </Typography>

                  <Controller
                    sx={{ m: 1, minWidth: "90%" }}
                    aria-invalid={!isDirty ? undefined : errors.type ? "true" : "false"}
                    control={control}
                    name="type"
                    render={({ field: {name} }) => (
                    <CustomFormEnumSelect
                        style={{ minWidth: '96%' }}
                        label={t('message.common.subtitle.actionType')}
                        isLabelView={false}
                        register={register}
                        name={name}
                        requiredMessage={t('message.common.error.text.actionType.select')}
                        // defaultValue={maintenanceTask && (maintenanceTask.type || '')}
                        // onChange={(e)=>{
                        //     setSubwayCode(e.target.value);
                        // }}
                        list={getEnumValueByEnumName("MaintenanceType")}
                    />
                    )}
                  />
                  {errors.type && <small role="alert"><br></br>{errors.type.message}</small>}
                </Box>
              </Grid2>
             
             {/* ================= 고장 분석 정보 ================= */}
              <SectionTitle title={t('message.common.title.failure.analysis.info')} />

              <Grid2>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                    {t('message.common.subtitle.breakdown.maintenance')} {t('message.common.subtitle.cause')}
                  </Typography>

                  <Controller
                    sx={{ m: 1, minWidth: "90%" }}
                    aria-invalid={!isDirty ? undefined : errors.trouble ? "true" : "false"}
                    control={control}
                    name="trouble"
                    render={({ field: {name} }) => (
                    <CustomFormEnumSelect
                        style={{ minWidth: '96%' }}
                        label={t('message.common.subtitle.breakdown.maintenance.cause')}
                        isLabelView={false}
                        register={register}
                        name={name}
                        requiredMessage={t('message.common.error.text.breakdown.maintenance.cause.select')}
                        // defaultValue={maintenanceTask && (maintenanceTask.trouble || '')}
                        list={getEnumValueByEnumName("EquipmentTrouble")}
                    />
                    )}
                  />
                  {errors.trouble && <small role="alert"><br></br>{errors.trouble.message}</small>}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                    {t('message.common.subtitle.main.management')}
                  </Typography>

                  <div style={{marginTop:"10px"}}>
                    <input type="checkbox" id="importantYn" {...register("importantYn", {
                    })}  />&nbsp;&nbsp;<label htmlFor="importantYn" style={{cursor:"pointer", color:"#000"}}></label>
                  </div>
                </Box>
              </Grid2>

              <Grid2>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                    {t('message.common.subtitle.maintenance.start.datetime')}
                  </Typography>

                  <Controller
                    control={control}
                    name="workingBgnAt"
                    rules={{
                      required: t('msg.enter.maintenance.start.datetime'),
                      validate: (value) => {
                        if (!value) return true;
                        // 만약 서버 포맷(T 포함)으로 이미 정제된 값이라면 통과
                        if (value.includes("T")) return true; 
                        
                        // 사용자가 입력창에 남겨둔 임시 텍스트 검증
                        const normalized = value.trim().replace(/-/g, ".");
                        const parsed = dayjs(normalized, "YYYY.MM.DD HH:mm", true);
                        
                        return (normalized.length === 16 && parsed.isValid()) || t('message.common.error.text.msg.invalid.date.format');
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <CustomMuiDateTimePicker field={field} fieldState={fieldState} />
                    )}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0, gap: 1.5 }}>
                    <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                      {t('message.common.subtitle.work.end.datetime')}
                    </Typography>
                    <Button 
                      variant="outlined" size="small" onClick={setEndAtNow}
                      sx={{ 
                        fontSize: "0.75rem", padding: "2px 8px", marginTop:"-8px", minWidth: "auto", color: "#02db02", borderColor: "#02db02",
                        "&:hover": { borderColor: "#256a20", backgroundColor: "rgba(48, 138, 42, 0.08)" }
                      }}
                    >
                      NOW
                    </Button>
                  </Box>

                  <Controller
                    control={control}
                    name="workingEndAt"
                    rules={{
                      // required: "작업시작일시를 입력하십시오.",
                      validate: (value) => {
                        if (!value) return true;
                        // 만약 서버 포맷(T 포함)으로 이미 정제된 값이라면 통과
                        if (value.includes("T")) return true; 
                        
                        // 사용자가 입력창에 남겨둔 임시 텍스트 검증
                        const normalized = value.trim().replace(/-/g, ".");
                        const parsed = dayjs(normalized, "YYYY.MM.DD HH:mm", true);
                        
                        return (normalized.length === 16 && parsed.isValid()) || t('message.common.error.text.msg.invalid.date.format');
                      }
                    }}
                    // rules={{ required: "작업 종료일시를 입력하십시오." }}
                    render={({ field, fieldState }) => (
                      <CustomMuiDateTimePicker field={field} fieldState={fieldState} />
                    )}
                  />
                </Box>
              </Grid2>

              <Grid2>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                    {t('message.common.subtitle.worker')} *
                  </Typography>

                  <TextField 
                    style={{width:"90%"}} id="outlined-basic" 
                    aria-invalid={!isDirty ? undefined : errors.worker ? "true" : "false"}
                    {...register("worker", {
                        required : t('message.common.error.text.worker.input')
                    })} 
                    key={'worker'}
                    defaultValue={name || ''}
                    sx={{...inputStyle, minWidth: '96%',
                      '& .MuiInputBase-root': {
                        height: '50px', // 고장원인 창의 실제 높이에 맞게 조절
                      },
                    }}
                  />
                  {errors.worker && <small role="alert"><br></br>{errors.worker.message}</small>}
                </Box>
                
                
              </Grid2>

              {/* ================= 투입 상세 ================= */}
              <SectionTitle title={t('message.common.subtitle.breakdown.input.details')} />

              <Box sx={boxStyle}>
                {inputParts.map((item) => (
                  // 🚀 행 자체를 독립된 컴포넌트로 렌더링하고 필요한 데이터와 핸들러를 넘깁니다.
                  <MaintenanceInputPartsRow
                    key={item.id}
                    item={item}
                    control={control}
                    register={register}
                    componentOptions={componentOptions} // 공통 목록 전달
                    locationOptions={locationOptions}   // 공통 목록 전달
                    onPartChange={handlePartChange}
                    onDelete={deleteInputPart}
                  />
                ))}
                
                <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 2, borderStyle: "dashed" }}
                    onClick={addInputPart}
                >
                    + {t('message.common.btn.add.detail.set')}
                </Button>
              </Box>

              <Box sx={{ flex: 1, mt: 2 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                  {t('message.common.subtitle.status.report.file')}
                  <AddCircleOutlineIcon fontSize="small" style={{cursor:"pointer", marginTop:"-3px", marginLeft:"5px"}} onClick={addInputFile}>+</AddCircleOutlineIcon>
                </Typography>
                
                {inputFiles && inputFiles.map(function(item, index) {
                return (
                <div key={index} style={{display:"flex", alignItems:"center", marginBottom:"6px"}}>
                    <input type="file" value={''} className="file-input" style={{width:"200px"}} onChange={e => handleInputFileChange(e, index)} />
                    { inputFiles[index] ? (
                        <RemoveCircleOutlineIcon fontSize="small" style={{cursor:"pointer", marginLeft:"5px"}} onClick={() => deleteInputFile(item.id)}>-</RemoveCircleOutlineIcon>
                    ) : ''}
                    {(item.file && item.file.name) ? item.file.name : (item.rFile && item.rFile.id && <><FileDownloadIcon file={item.rFile} viewFileName={true} /></>)}
                </div>
                )
                })}
              </Box>
              
              <Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#a0aec0", mb: 1 }}>
                  {t('message.common.subtitle.alertDetail')} / {t('message.common.subtitle.work.detail')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  {...register("remark", { required: t('message.common.error.text.work.detail.input') })} 
                  rows={4}
                  placeholder={t('message.common.text.placeholder.action.result.and.remarks')}
                  sx={{
                    textarea: { color: "#e2e8f0" },
                    "& .MuiOutlinedInput-root": { backgroundColor: "#0f172a" },
                  }}
                />
                {errors.remark && <small role="alert"><br/>{errors.remark.message}</small>}
              </Box>
            </form>
          </CardContent>

          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
            {/* <Button variant="contained" onClick={handleSubmit(onSubmit)}>
              등록
            </Button> */}
            {((type && type === "modify" && maintenanceTask && maintenanceTask.workingBgnAt) || (maintenanceTask === null || (maintenanceTask && !maintenanceTask.workingBgnAt))) &&
            <LoadingButton variant="contained" size="small" color="primary" loading={fileLoading} onClick={handleSubmit(onSubmit)}>
              {(type && type==="modify" && maintenanceTask && maintenanceTask.workingBgnAt) ? t('message.common.btn.modify') : (maintenanceTask === null || (maintenanceTask && !maintenanceTask.workingBgnAt)) ? t('message.common.btn.write') : ""}
            </LoadingButton>
            }
            <Button variant="contained" onClick={handleClose} color="inherit">
              {t('message.common.btn.cancel')}
            </Button>
          </Stack>
        </MainCard>
      </Modal>
    </>
  );
};

export default MaintenanceTaskAndAlertModal;
